import { useState, useEffect, useRef } from 'react';
import { FileText, Package, ShoppingCart, Loader2, Calendar, Download, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Order, OrderItem } from '../types/orders';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  Scale,
  ScaleOptionsByType,
  CartesianScaleTypeRegistry
} from 'chart.js';
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval, subMonths, startOfYear, isAfter } from 'date-fns';
import { generateEODReportPDF } from '../utils/reports';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SoldProduct {
  id: number;
  name: string;
  platform?: string;
  quantity_sold: number;
  total_value: number;
  price: number;
}

interface DailySales {
  date: string;
  total: number;
}

type TimeframeOption = '7d' | '14d' | '30d' | '60d' | '90d' | '180d' | '360d' | 'YTD';

export default function EODReports() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [soldProducts, setSoldProducts] = useState<SoldProduct[]>([]);
  const [totalSales, setTotalSales] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dailySales, setDailySales] = useState<DailySales[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>('7d');
  const [showTimeframeSelector, setShowTimeframeSelector] = useState(false);
  const chartRef = useRef<ChartJS<'line'>>(null);

  useEffect(() => {
    fetchSelectedDayData();
    fetchSalesData(selectedTimeframe);
  }, [selectedDate, selectedTimeframe]);

  const fetchSalesData = async (timeframe: TimeframeOption) => {
    try {
      const endDate = endOfDay(new Date());
      let startDate: Date;
      
      // Determine the start date based on the selected timeframe
      switch (timeframe) {
        case '7d':
          startDate = subDays(startOfDay(endDate), 7);
          break;
        case '14d':
          startDate = subDays(startOfDay(endDate), 14);
          break;
        case '30d':
          startDate = subDays(startOfDay(endDate), 30);
          break;
        case '60d':
          startDate = subDays(startOfDay(endDate), 60);
          break;
        case '90d':
          startDate = subDays(startOfDay(endDate), 90);
          break;
        case '180d':
          startDate = subDays(startOfDay(endDate), 180);
          break;
        case '360d':
          startDate = subDays(startOfDay(endDate), 360);
          break;
        case 'YTD':
          startDate = startOfYear(endDate);
          break;
        default:
          startDate = subDays(startOfDay(endDate), 7);
      }
      
      const { data: salesData, error } = await supabase
        .from('orders')
        .select('created_at, total_amount')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) throw error;

      // Create an array of all days in the interval
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      
      // Initialize sales data for each day
      const dailySalesMap = new Map<string, number>();
      days.forEach(day => {
        dailySalesMap.set(format(day, 'yyyy-MM-dd'), 0);
      });

      // Sum up sales for each day
      salesData?.forEach(order => {
        const date = format(new Date(order.created_at), 'yyyy-MM-dd');
        dailySalesMap.set(date, (dailySalesMap.get(date) || 0) + order.total_amount);
      });

      // Convert map to array and sort by date
      const salesArray = Array.from(dailySalesMap.entries()).map(([date, total]) => ({
        date,
        total
      })).sort((a, b) => a.date.localeCompare(b.date));

      setDailySales(salesArray);
    } catch (err) {
      console.error('Error fetching sales data:', err);
    }
  };

  const fetchSelectedDayData = async () => {
    try {
      const dayStart = startOfDay(selectedDate);
      const dayEnd = endOfDay(selectedDate);

      // Fetch selected day's orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            product_id,
            quantity,
            price_at_time,
            products (*)
          )
        `)
        .gte('created_at', dayStart.toISOString())
        .lt('created_at', dayEnd.toISOString())
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Calculate total sales
      const total = ordersData.reduce((sum, order) => sum + order.total_amount, 0);
      setTotalSales(total);
      setOrders(ordersData);

      // Process sold products
      const soldProductsMap = new Map<number, SoldProduct>();
      
      ordersData.forEach(order => {
        order.order_items.forEach((item: OrderItem) => {
          const productId = item.product_id;
          const productName = item.products.name;
          const productPlatform = item.products.platform;
          const quantity = item.quantity;
          const value = item.price_at_time * quantity;

          if (soldProductsMap.has(productId)) {
            const existingProduct = soldProductsMap.get(productId)!;
            existingProduct.quantity_sold += quantity;
            existingProduct.total_value += value;
          } else {
            soldProductsMap.set(productId, {
              id: productId,
              name: productName,
              platform: productPlatform,
              quantity_sold: quantity,
              total_value: value,
              price: item.price_at_time
            });
          }
        });
      });

      const soldProducts: SoldProduct[] = Array.from(soldProductsMap.values());
      setSoldProducts(soldProducts);
    } catch (err) {
      console.error('Error fetching daily data:', err);
    } finally {
      setLoading(false);
    }
  };

  // New function to handle PDF export
  const handleExportPDF = () => {
    try {
      setIsExporting(true);
      
      // Generate the PDF using our utility function
      const pdfDataUrl = generateEODReportPDF(
        selectedDate,
        orders,
        soldProducts,
        totalSales,
        dailySales
      );
      
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = pdfDataUrl;
      link.download = `BVG_EOD_Report_${format(selectedDate, 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getTimeframeLabel = (timeframe: TimeframeOption): string => {
    switch (timeframe) {
      case '7d': return 'Last 7 Days';
      case '14d': return 'Last 14 Days';
      case '30d': return 'Last 30 Days';
      case '60d': return 'Last 60 Days';
      case '90d': return 'Last 90 Days';
      case '180d': return 'Last 180 Days';
      case '360d': return 'Last 360 Days';
      case 'YTD': return 'Year to Date';
      default: return 'Last 7 Days';
    }
  };

  const handleChartClick = (event: any, elements: any) => {
    if (elements.length > 0) {
      const clickedIndex = elements[0].index;
      const clickedDate = new Date(dailySales[clickedIndex].date);
      
      // Only set date if it's a past date (not in the future)
      if (isAfter(new Date(), clickedDate)) {
        setSelectedDate(clickedDate);
      }
    }
  };

  const chartData = {
    labels: dailySales.map(sale => format(new Date(sale.date), 'MMM d')),
    datasets: [
      {
        label: 'Daily Sales',
        data: dailySales.map(sale => sale.total),
        fill: true,
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 10
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.raw) {
              return `£${(context.raw as number).toFixed(2)}`;
            }
            return '';
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear',
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          padding: 8,
          callback: function(value) {
            if (typeof value === 'number') {
              return `£${value.toFixed(0)}`;
            }
            return value;
          },
        },
      },
      x: {
        type: 'category',
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          padding: 8,
        },
      },
    },
    onClick: handleChartClick
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <>
      {showDatePicker && (
        <div className="fixed inset-0 z-[100]" onClick={() => setShowDatePicker(false)}>
          <div 
            className="absolute p-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl"
            style={{
              top: '155px',
              right: '5.5rem'
            }}
            onClick={e => e.stopPropagation()}
          >
            <input
              type="date"
              value={format(selectedDate, 'yyyy-MM-dd')}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => {
                if (e.target.value) {
                  const date = new Date(e.target.value);
                  date.setHours(12, 0, 0, 0);
                  setSelectedDate(date);
                  setShowDatePicker(false);
                }
              }}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded text-white"
            />
          </div>
        </div>
      )}
      
      {showTimeframeSelector && (
        <div className="fixed inset-0 z-[100]" onClick={() => setShowTimeframeSelector(false)}>
          <div 
            className="absolute left-[400px] top-[300px] w-72 bg-white/[0.02] backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 space-y-4">
              <h3 className="text-white/90 font-medium">Select Timeframe</h3>
              <div className="grid grid-cols-1 gap-2">
                {(['7d', '14d', '30d', '60d', '90d', '180d', '360d', 'YTD'] as TimeframeOption[]).map(timeframe => (
                  <button
                    key={timeframe}
                    onClick={() => {
                      setSelectedTimeframe(timeframe);
                      setShowTimeframeSelector(false);
                    }}
                    className={`w-full h-12 rounded-xl flex items-center justify-center px-4 backdrop-blur-xl border transition-all duration-200 ${
                      selectedTimeframe === timeframe
                        ? 'bg-purple-500/20 border-purple-500/30 text-purple-400'
                        : 'bg-white/[0.02] border-white/[0.1] text-white/90 hover:bg-purple-500/10 hover:border-purple-500/20'
                    }`}
                  >
                    <span className="font-medium">{getTimeframeLabel(timeframe)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto">
        <div className="p-6">
          {/* Header Bar */}
          <div className="relative z-[50] flex items-center justify-between mb-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500/10 rounded-lg p-2">
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Sales Analytics</h1>
                <p className="text-sm text-white/50">
                  Viewing data for {format(selectedDate, 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
            
            {/* Date Navigation and Export Button */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 1);
                  setSelectedDate(newDate);
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <span className="text-white">←</span>
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg transition-colors border border-white/20"
                >
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {format(selectedDate, 'MMM d, yyyy')}
                  </span>
                </button>
              </div>

              <button
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 1);
                  if (newDate <= new Date()) {
                    setSelectedDate(newDate);
                  }
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                disabled={selectedDate >= new Date()}
              >
                <span className="text-white">→</span>
              </button>
              
              {/* Export Button */}
              <button
                onClick={handleExportPDF}
                disabled={isExporting || orders.length === 0}
                className={`ml-4 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  isExporting || orders.length === 0
                    ? 'bg-gray-600/50 text-white/50 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Main Stats */}
            <div className="col-span-8 space-y-6">
              {/* Key Metrics Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="h-[140px] bg-gradient-to-br from-red-300/20 to-red-400/20 backdrop-blur-xl border border-white/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white/70">Total Sales</p>
                      <h3 className="text-2xl font-bold text-white mt-1">£{totalSales.toFixed(2)}</h3>
                    </div>
                    <div className="bg-purple-500/10 rounded-lg p-2">
                      <FileText className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-white/50">
                    {orders.length} orders today
                  </div>
                </div>

                <div className="h-[140px] bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-xl border border-white/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white/70">Items Sold</p>
                      <h3 className="text-2xl font-bold text-white mt-1">
                        {soldProducts.reduce((sum, p) => sum + p.quantity_sold, 0)}
                      </h3>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-2">
                      <Package className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-white/50">
                    {soldProducts.length} unique products
                  </div>
                </div>

                <div className="h-[140px] bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-xl border border-white/20 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white/70">Avg Order Value</p>
                      <h3 className="text-2xl font-bold text-white mt-1">
                        £{orders.length > 0 ? (totalSales / orders.length).toFixed(2) : '0.00'}
                      </h3>
                    </div>
                    <div className="bg-emerald-500/10 rounded-lg p-2">
                      <ShoppingCart className="w-5 h-5 text-emerald-400" />
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-white/50">
                    {(orders.length > 0 
                      ? (soldProducts.reduce((sum, p) => sum + p.quantity_sold, 0) / orders.length).toFixed(1) 
                      : '0')} items per order
                  </div>
                </div>
              </div>

              {/* Sales Chart */}
              <div className="h-[400px] bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-white">Revenue Trend</h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-white/50">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span>{getTimeframeLabel(selectedTimeframe)}</span>
                    </div>
                    <button
                      onClick={() => setShowTimeframeSelector(true)}
                      className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-sm rounded-lg transition-colors border border-white/10 flex items-center gap-2"
                    >
                      <Clock className="w-4 h-4" />
                      Change Timeframe
                    </button>
                  </div>
                </div>
                <div className="h-[300px]">
                  <Line 
                    ref={chartRef}
                    data={chartData} 
                    options={{
                      ...chartOptions,
                      maintainAspectRatio: false,
                      responsive: true,
                      layout: {
                        padding: {
                          left: 10,
                          right: 30,
                          top: 20,
                          bottom: 10
                        }
                      }
                    }} 
                  />
                </div>
              </div>

              {/* Products Table */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Top Selling Products</h2>
                  <div className="text-sm text-white/50">
                    {soldProducts.length} products sold today
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {soldProducts
                      .sort((a, b) => b.total_value - a.total_value)
                      .slice(0, 5)
                      .map((product, index) => (
                      <div
                        key={product.id}
                        className="flex items-center gap-4 p-4 bg-white/5 rounded-lg"
                      >
                        <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg text-white/70">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-medium">
                            {product.name}
                            {product.platform && (
                              <span className="ml-2 text-white/50">({product.platform})</span>
                            )}
                          </h3>
                          <p className="text-white/50 text-sm mt-0.5">
                            {product.quantity_sold} units at £{product.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">£{product.total_value.toFixed(2)}</p>
                          <p className="text-white/50 text-sm">
                            {((product.total_value / totalSales) * 100).toFixed(1)}% of sales
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Orders & Analytics */}
            <div className="col-span-4 space-y-6">
              {/* Sales Insights */}
              <div className="h-[565px] bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent hover:scrollbar-thumb-white/30">
                <h2 className="text-lg font-semibold text-white mb-6">Performance Insights</h2>
                <div className="space-y-6">
                  {/* Peak Hours */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-white/70">Peak Sales Hour</h3>
                      <span className="text-sm text-white/50">Today</span>
                    </div>
                    <p className="text-xl font-semibold text-white">
                      {orders.length > 0 
                        ? format(new Date(orders.reduce((max, order) => {
                            const hour = new Date(order.created_at).getHours();
                            const count = orders.filter(o => new Date(o.created_at).getHours() === hour).length;
                            return count > max.count ? { hour, count } : max;
                          }, { hour: 0, count: 0 }).hour * 3600000), 'h:mm a')
                        : 'N/A'
                      }
                    </p>
                  </div>

                  {/* Platform Distribution */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-white/70">Platform Distribution</h3>
                      <span className="text-sm text-white/50">By Units Sold</span>
                    </div>
                    <div className="space-y-3">
                      {['PlayStation 5', 'PlayStation 4', 'Xbox Series X/S', 'Xbox One', 'Nintendo Switch', 'PC']
                        .map(platform => {
                          const count = soldProducts
                            .filter(p => p.platform === platform)
                            .reduce((sum, p) => sum + p.quantity_sold, 0);
                          const total = soldProducts.reduce((sum, p) => sum + p.quantity_sold, 0);
                          const percentage = total > 0 ? (count / total) * 100 : 0;
                          
                          return (
                            <div key={platform}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-white">{platform}</span>
                                <span className="text-white/70">{count} units</span>
                              </div>
                              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-purple-500 to-purple-600"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                      })}
                    </div>
                  </div>

                  {/* Points Usage */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-white/70">Loyalty Points</h3>
                      <span className="text-sm text-white/50">Today</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xl font-semibold text-white">
                          {orders.reduce((sum, order) => sum + (order.points_used || 0), 0)}
                        </p>
                        <p className="text-sm text-white/50">Points Used</p>
                      </div>
                      <div>
                        <p className="text-xl font-semibold text-white">
                          {orders.reduce((sum, order) => sum + (order.points_earned || 0), 0)}
                        </p>
                        <p className="text-sm text-white/50">Points Earned</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10">
                  <h2 className="text-lg font-semibold text-white">Recent Orders</h2>
                </div>
                <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-purple-500/20 scrollbar-track-transparent hover:scrollbar-thumb-purple-500/30">
                  <div className="divide-y divide-white/10">
                    {orders.map(order => (
                      <div key={order.id} className="p-4 hover:bg-white/5 transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-white font-medium">Order #{order.id}</h3>
                            <p className="text-white/50 text-sm">
                              {format(new Date(order.created_at), 'h:mm a')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">£{order.total_amount.toFixed(2)}</p>
                            <p className="text-white/50 text-sm">
                              {order.order_items.length} items
                            </p>
                          </div>
                        </div>
                        {order.points_used > 0 && (
                          <div className="mt-2 text-sm">
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full">
                              {order.points_used} points used
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}